import { AppBox, AppGridBox, AppLink, AppText } from "../../../../../commons/components";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { AxiosError } from "axios";
import { useLayoutEffect, useState } from "react";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import AppBorderBox from "../../../../../commons/components/BorderBox";
import OrganizerBasicInfoAddEdit from "./OrganizerBasicInfoAddEdit";
import { cleanUpStepperFormData, setStepperFormData } from "../../../../../redux/slices/stepper-form-slice";
import { getErrorKeyPosition } from "../../../../../commons/utilities/form-utils";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import FormImageAddEdit from "../../../FormImageAddEdit";
import isNullOrUndefined from "../../../../../commons/utilities/null-or-undefined";
import { IFootballOrganizerDTO } from "../../../../../api-services/football/admin/manage-football-organizer-service";
import { IFootballOrganizer } from "../../../../../commons/models/football/interface/i-football-organizer";
import { footballOrganizerEdit } from "../../../../../redux/slices/football/admin/organizer/manage-football-organizer-edit-slice";
import { footballOrganizerAdd } from "../../../../../redux/slices/football/admin/organizer/manage-football-organizer-add-slice";

interface FootballOrganizerAddEditProps {
  footballOrganizerData?: IFootballOrganizer | null;
  onCancel?: () => void;
  onSave: () => void;
}

const stepperFormOptions = [
  {
    step: 1,
    title: "Organizer's Logo",
  },
  {
    step: 2,
    title: "Basic Information",
  },
];

const stepperFromKeyMap = [["logo"], ["name", "email", "country", "website"]];

export default function FootballOrganizerAddEdit({ footballOrganizerData, onSave }: FootballOrganizerAddEditProps) {
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const [selectedStepperFormOption, setSelectedStepperFormOption] = useState(stepperFormOptions[0]);
  const formData = useAppSelector<IFootballOrganizerDTO>((state) => state.stepperForm.stepperFormData);
  const { footballOrganizerAddStatus } = useAppSelector((state) => state.manageFootballOrganizerAdd);
  const { footballOrganizerEditStatus } = useAppSelector((state) => state.manageFootballOrganizerEdit);
  const { uploadedImageStatus } = useAppSelector((state) => state.imageUpload);

  //initiate the stepperData
  useLayoutEffect(() => {
    if (!footballOrganizerData) {
      const footballOrganizerData: IFootballOrganizerDTO = {
        name: "",
        email: "",
        logo: null,
        country: null,
        website: null,
      };
      dispatch(setStepperFormData<IFootballOrganizerDTO>(footballOrganizerData));
    }
  }, []);

  useLayoutEffect(() => {
    if (footballOrganizerData) {
      const footballOrganizerDTO: IFootballOrganizerDTO = {
        name: footballOrganizerData.name,
        email: footballOrganizerData.email,
        logo: footballOrganizerData.logo,
        country: footballOrganizerData.country,
        website: footballOrganizerData.website,
      };
      dispatch(setStepperFormData<IFootballOrganizerDTO>(footballOrganizerDTO));
    }
  }, [footballOrganizerData]);

  const handleNext = (currentStep: number) => {
    if (
      footballOrganizerAddStatus !== StatusEnum.Loading &&
      footballOrganizerEditStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep]);
    }
  };
  const handleBack = (currentStep: number) => {
    if (
      footballOrganizerAddStatus !== StatusEnum.Loading &&
      footballOrganizerEditStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep - 2]);
    }
  };

  const handleStepperOptionClick = (option: any) => {
    if (
      footballOrganizerAddStatus !== StatusEnum.Loading &&
      footballOrganizerEditStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(option);
    }
  };

  const handleFormSubmit = () => {
    const formattedValues: IFootballOrganizerDTO = {
      ...formData,
      logo: formData.logo ? formData.logo : null,
      email: formData.email ? formData.email : null,
      country: formData.country ? formData.country : null,
      website: formData.website ? formData.website : null,
    };
    dispatch(
      footballOrganizerData
        ? footballOrganizerEdit({
            organizerId: footballOrganizerData.id,
            organizerData: formattedValues,
          })
        : footballOrganizerAdd({ organizerData: formattedValues })
    )
      .unwrap()
      .then(() => {
        if (footballOrganizerData) {
          addSnackbar({
            key: "footballOrganizer-edit-success",
            text: "Organizer Edited Successfully",
            variant: "success",
          });
        } else {
          addSnackbar({
            key: "footballOrganizer-add-success",
            text: "Organizer Added Successfully",
            variant: "success",
          });
        }
        dispatch(cleanUpStepperFormData());
        onSave();
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        const responseData = error.response?.data;
        if (error.response?.status === 417) {
          addSnackbar({
            key: "error",
            text: "Form not valid",
            variant: "danger",
          });
          if (responseData) {
            const stepperPositionSet = new Set<number>();
            Object.entries(responseData).forEach(([field, messages]) => {
              messages.forEach((message: string) => {
                methods.setError(field, { message });
                const stepperPosition = getErrorKeyPosition(field, stepperFromKeyMap);
                if (stepperPosition) {
                  stepperPositionSet.add(stepperPosition.step);
                }
                const lowestStep = Math.min(...Array.from(stepperPositionSet));
                setSelectedStepperFormOption(stepperFormOptions[lowestStep]);
              });
            });
          }
        } else {
          addSnackbar({
            key: "error",
            text: responseData?.message,
            variant: "danger",
          });
        }
      });
  };

  const clearErrorField = (key: string) => {
    methods.clearErrors(key);
  };

  return (
    <FormProvider {...methods}>
      <AppBox flexDirection="column" gap="sm" style={{ height: "var(--modal-md)" }}>
        <AppGridBox className=" flex-1" style={{ gridTemplateColumns: "1fr 3fr" }} gap="sm">
          <AppBorderBox border={["Right"]}>
            <AppBox flexDirection="column" gap="2xs" className="w-100">
              {stepperFormOptions.map((option, index) => (
                <AppLink
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStepperOptionClick(option);
                  }}
                >
                  <AppBorderBox
                    border={option.step === selectedStepperFormOption.step ? ["Right"] : []}
                    borderColor="primary"
                  >
                    <AppBox pl="sm" py="2xs">
                      <AppText
                        fontWeight="semibold"
                        size="lg"
                        color={option.step === selectedStepperFormOption.step ? "primary" : "currentColor"}
                      >
                        {option.title}
                      </AppText>
                    </AppBox>
                  </AppBorderBox>
                </AppLink>
              ))}
            </AppBox>
          </AppBorderBox>
          <AppBox flexDirection="column" className="w-100 flex-1">
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 1 ? { display: "flex" } : { display: "none" }}
            >
              <FormImageAddEdit
                image={footballOrganizerData?.logo}
                DTOKey="logo"
                title="Organizer's Logo"
                isEdit={!isNullOrUndefined(footballOrganizerData)}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onSkipOrNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={footballOrganizerEditStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 2 ? { display: "flex" } : { display: "none" }}
            >
              <OrganizerBasicInfoAddEdit
                footballOrganizerData={footballOrganizerData}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onBack={handleBack}
                onSubmit={handleFormSubmit}
                onClearError={clearErrorField}
                submitStatus={footballOrganizerEditStatus}
              />
            </AppBox>
          </AppBox>
        </AppGridBox>
      </AppBox>
    </FormProvider>
  );
}
